import { createContext, type PropsWithChildren, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import posthog from 'posthog-js';

import type { Auth } from '@/entities/auth';
import { AutoLogin } from '@/providers/auth/auto-login';
import { AutoOrgLogin } from '@/providers/auth/auto-org-login';
import { stateFromAuth0 } from '@/providers/auth/get-state-from-auth0';
import { recoverFromAuth0OauthError } from '@/providers/auth/recover-from-auth0-oauth-error';
import { UnreachableCaseError } from '@/utils/errors/unreachable-case-error';

export type AuthProviderProps = PropsWithChildren<{
  loadingComponent: ReactNode;
}>;

export const AuthContext = createContext<Auth>(
  // @ts-expect-error - This is a placeholder value that should never be used
  null,
);

export const currentOrganizationStorageKey = 'authCurrentOrganization';

export function AuthProvider({
  children,
  loadingComponent,
}: AuthProviderProps) {
  const auth0 = useAuth0();

  const stateQuery = useQuery({
    queryKey: ['authState', auth0],
    queryFn: () => stateFromAuth0(auth0),
  });

  if (stateQuery.isLoading) {
    return <>{loadingComponent}</>;
  }

  if (stateQuery.error) {
    if (recoverFromAuth0OauthError(stateQuery.error)) {
      return null;
    }
    throw stateQuery.error;
  }

  if (auth0.isLoading) {
    return <>{loadingComponent}</>;
  }

  if (auth0.error) {
    if (recoverFromAuth0OauthError(auth0.error)) {
      return null;
    }
    throw auth0.error;
  }

  const state = stateQuery.data!;

  switch (state.type) {
    case 'notAuthenticated':
      return <AutoLogin />;
    case 'authenticated':
      return <AutoOrgLogin orgs={state.organizations} />;
    case 'orgAuthenticated':
      return (
        <AuthContext.Provider
          value={{
            async logout(returnTo) {
              await auth0.logout({ logoutParams: { returnTo } });
              sessionStorage.clear();
              localStorage.clear();
              posthog.reset();
            },
            async getAccessToken() {
              return auth0.getAccessTokenSilently();
            },
            async switchOrganization(newOrganization) {
              await auth0.loginWithRedirect({
                authorizationParams: {
                  organization: newOrganization,
                },
              });
            },
          }}
        >
          {children}
        </AuthContext.Provider>
      );
    default:
      throw new UnreachableCaseError(state);
  }
}
